import React, { useState, useEffect } from 'react';
import {
  Checkbox,
  Grid,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useDispatch } from 'react-redux';
import SimpleLineChart from './components/LineChart';
import { getTeamSummary } from '../../../../../../../lib/api/dashboard';
import CenteredCircularProgress from '../../../../../../../lib/designSystem/CircularProgress';
import EnhacedBox from '../../../../../../../lib/designSystem/BigBox';
import CustomPieChart from '../../../../../../../lib/designSystem/PieChart';
import EnhancedTable from '../../../../../../../lib/designSystem/Table';

const colorsArray = [
  '#0F23DF',
  '#03B4DC',
  '#FFC400',
  '#07D2FE',
  '#D500F9',
  '#651FFF',
  '#FF5722',
  '#00BCD4',
  '#E91E63',
  '#3F51B5',
  '#FFEB3B',
  '#00E676',
  '#673AB7',
  '#1DE9B6',
  '#FF9100',
  '#009688',
  '#D32F2F',
  '#303F9F',
  '#FFD600',
  '#00C853',
  '#9C27B0',
  '#76FF03',
  '#FF3D00',
  '#00BFA5',
  '#C51162',
  '#536DFE',
  '#FFEA00',
  '#64DD17',
  '#8E24AA',
  '#64FFDA',
  '#FF6F00',
  '#004D40',
  '#880E4F',
  '#1A237E',
  '#FFAB00',
  '#00B8D4',
  '#6200EA',
  '#00BFA5',
];

const TeamSummary = () => {
  const dispatch = useDispatch();
  const [cessionsSummary, setCessionsSummary] = useState({
    graph: [],
    businessTable: [],
    factoringTable: [],
    last6Months: [],
    summary: {
      lastMonth: [],
    },
  });
  const [loading, setLoading] = useState(false);
  const [selectedMonths, setSelectedMonths] = useState<any>([]);
  const [byUsersTable, setByUsersTable] = useState<any>([]);
  const [filteredByUsersTable, setFilteredByUsersTable] = useState<any>([]);
  const [filteredChartData, setFilteredChartData] = useState<any>([]);
  const [chartDataFilters, setChartDataFilters] = useState<any>([]);
  const [selectedChartDataFilter, setSelectedChartDataFilter] = useState<any>(
    [],
  );

  const fetchCessions = async () => {
    setLoading(true);
    try {
      const cessionsSummaryResponse = await getTeamSummary(dispatch);
      setByUsersTable(cessionsSummaryResponse.byUsersTable);
      setFilteredByUsersTable([...cessionsSummaryResponse.byUsersTable]);
      setCessionsSummary(cessionsSummaryResponse || []);
      setFilteredChartData(cessionsSummaryResponse.graph);
      const chartDataOptions = Object.keys(
        cessionsSummaryResponse.graph[0] || {},
      ).filter((key) => key !== 'name');
      setChartDataFilters(chartDataOptions);
      setSelectedChartDataFilter(chartDataOptions);
    } catch (error) {
      console.log(error);
    }
    setLoading(false);
  };

  const handleSelectChartDataFilter = (selectedFilters: any) => {
    if (selectedFilters.includes('all')) {
      setSelectedChartDataFilter(chartDataFilters);
      setFilteredChartData(cessionsSummary.graph);
      setFilteredByUsersTable(byUsersTable);
      return;
    } else if (selectedFilters.includes('none')) {
      setSelectedChartDataFilter([]);
      setFilteredChartData([]);
      setFilteredByUsersTable([]);
      return;
    } else {
      setSelectedChartDataFilter(selectedFilters);
      const data = cessionsSummary.graph;
      const newData = data.map((item: any) => {
        const newItem: any = { name: item.name };
        for (const filter of selectedFilters) {
          newItem[filter] = item[filter];
        }
        return newItem;
      });
      setFilteredChartData(newData);
      const filteredByUsersTable = byUsersTable.filter((item: any) =>
        selectedFilters.includes(item.name),
      );
      setFilteredByUsersTable(filteredByUsersTable);
    }
  };

  useEffect(() => {
    fetchCessions();
  }, []);

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <Typography variant="h5">Resumen equipo</Typography>
      <Grid container spacing={2}>
        <Grid item xs={8}>
          <EnhacedBox>
            <div
              style={{
                padding: '0px 8px 10px 8px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div>
                <Typography variant="body2">
                  Selecciona los gráficos que deseas ver
                </Typography>
              </div>
              <div>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simplex-select"
                  multiple
                  value={selectedChartDataFilter}
                  onChange={(event) =>
                    handleSelectChartDataFilter(event.target.value)
                  }
                  renderValue={(selectedChartDataFilter) =>
                    selectedChartDataFilter.join(', ')
                  }
                  fullWidth
                  style={{ width: '300px', height: '30px' }}
                >
                  <MenuItem value="all">
                    <ListItemText primary="Todos" />
                  </MenuItem>
                  <MenuItem value="none">
                    <ListItemText primary="Ninguno" />
                  </MenuItem>
                  {chartDataFilters.sort().map((data: any, index: any) => (
                    <MenuItem key={data} value={data}>
                      <Checkbox
                        checked={selectedChartDataFilter.indexOf(data) > -1}
                      />
                      <ListItemText
                        primary={data}
                        style={{
                          color: colorsArray[index % colorsArray.length],
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </div>
            </div>
            <SimpleLineChart
              aspect={4.0 / 1.5}
              data={filteredChartData}
              selectedMonths={selectedMonths}
              setSelectedMonths={setSelectedMonths}
            />
          </EnhacedBox>
        </Grid>
        <Grid item xs={4}>
          <EnhacedBox>
            <div style={{ minHeight: 348 }}>
              <Typography variant="body1" fontWeight={'bold'}>
                Este mes...
              </Typography>
              <div style={{ padding: '6px 0px' }}>
                <CustomPieChart
                  data={cessionsSummary.summary.lastMonth}
                  money
                />
              </div>
            </div>
          </EnhacedBox>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="h5">Factorizado por ejecutivo</Typography>
          <EnhancedTable
            data={filteredByUsersTable}
            dataHeaders={[
              { key: 'name', label: 'Ejecutivo' },
              ...(cessionsSummary.last6Months.map((x: any) => ({
                key: x,
                label: x,
                type: 'money',
              })) || []),
            ]}
            finalSumRow
            exportToFile="xlsx"
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default TeamSummary;
